import React from 'react';
import { Check } from 'lucide-react';
import { offers } from '../data/mock';
import { ScrollReveal } from './ScrollAnimations';
import { MagneticButton } from './MagneticElements';
import { ShimmerButton, GlassButton } from './PremiumButtons';

const OffersSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="offers" className="relative bg-black py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <ScrollReveal animation="fadeUp">
            <span className="text-accent text-sm font-medium tracking-wider uppercase mb-4 block">
              Nos offres
            </span>
          </ScrollReveal>
          <ScrollReveal animation="fadeUp" delay={100}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Des solutions adaptées à vos ambitions
            </h2>
          </ScrollReveal>
          <ScrollReveal animation="fadeUp" delay={200}>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Chaque projet est unique. Découvrez nos formules flexibles, conçues pour répondre à tous vos besoins.
            </p>
          </ScrollReveal>
        </div>

        {/* Offers Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {offers.map((offer, index) => (
            <ScrollReveal
              key={offer.id}
              animation="fadeUp"
              delay={index * 150}
              className={`relative p-8 flex flex-col transition-all duration-500 group ${
                offer.highlight
                  ? 'bg-gradient-to-b from-accent/10 to-accent/5 border-2 border-accent/30 hover:border-accent/60'
                  : 'bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.15] hover:bg-white/[0.04]'
              }`}
            >
              {/* Highlight Badge */}
              {offer.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-black text-xs font-bold uppercase tracking-wider">
                  Populaire
                </div>
              )}

              {/* Glow effect for highlighted card */}
              {offer.highlight && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10">
                  <div className="absolute inset-0 bg-accent/10 blur-xl" />
                </div>
              )}

              {/* Content */}
              <h3 className={`text-2xl font-semibold mb-4 ${
                offer.highlight ? 'text-accent' : 'text-white group-hover:text-accent transition-colors duration-300'
              }`}>
                {offer.title}
              </h3>
              <p className="text-white/50 mb-8 leading-relaxed flex-grow">
                {offer.description}
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {offer.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center gap-3 text-white/70 text-sm">
                    <span className={`w-5 h-5 flex items-center justify-center ${
                      offer.highlight ? 'bg-accent/20 text-accent' : 'bg-white/10 text-white/50'
                    }`}>
                      <Check size={12} />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA - Premium Buttons */}
              <MagneticButton
                as="div"
                onClick={scrollToContact}
                strength={0.3}
                className="w-full"
              >
                {offer.highlight ? (
                  <ShimmerButton onClick={scrollToContact} className="w-full">
                    Demander un devis
                  </ShimmerButton>
                ) : (
                  <GlassButton onClick={scrollToContact} className="w-full">
                    Demander un devis
                  </GlassButton>
                )}
              </MagneticButton>
            </ScrollReveal>
          ))}
        </div>

        {/* Bottom Note */}
        <ScrollReveal animation="fadeUp" delay={500}>
          <p className="text-center text-white/40 text-sm mt-12">
            Tous nos projets sont réalisés sur-mesure. Contactez-nous pour un devis personnalisé.
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default OffersSection;
